/**
 * Virtual plan generators.
 *
 * The planning UI shows "next N months" and "next N weeks" as clickable
 * rows even if no row exists in the DB yet. These helpers produce those
 * virtual rows and the week-numbering rule used when we materialise a
 * virtual week into a real `weekly_plans` record.
 *
 * Rules:
 *   - Weeks run Monday → Sunday.
 *   - A week "belongs to" the month that contains its Monday. So
 *     the week Apr 27 – May 3 belongs to April; the week May 4 – May 10
 *     belongs to May.
 *   - `week_number` within a month is the ordinal position of the week
 *     among Mondays-in-that-month (1-based). A month has at most 5
 *     Mondays, which fits inside the `week_number 1..6` CHECK in the
 *     schema with room to spare.
 *   - Month anchor: the current real-world month. Week anchor: the Monday
 *     of the current real-world week (so "this week" is always row #1).
 */

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

export interface VirtualMonth {
    /** 4-digit year. */
    year: number;
    /** 1..12 (human month, matches `monthly_plans.month`). */
    month: number;
    /** e.g. "April 2026". Used as the default `monthly_plans.name`. */
    name: string;
    /** Stable key for React lists: "YYYY-MM". */
    key: string;
}

export interface VirtualWeek {
    /** Year of the week's Monday. */
    year: number;
    /** 1..12, the month of the week's Monday (this is the month the week belongs to). */
    month: number;
    /** 1..5, the ordinal position of this week among Mondays-in-its-month. */
    weekNumber: number;
    /** ISO date string (YYYY-MM-DD) for the Monday. */
    startDate: string;
    /** ISO date string (YYYY-MM-DD) for the Sunday 6 days later. */
    endDate: string;
    /** e.g. "Week 3". Used as the default `weekly_plans.name`. */
    name: string;
    /** Stable key for React lists: the Monday's ISO date. */
    key: string;
}

/** Copy a Date without mutating the input. */
function cloneDate(d: Date): Date {
    return new Date(d.getTime());
}

/** Format a Date as YYYY-MM-DD using local time (matches what the backend stores as DATE). */
function toIsoDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/**
 * Return the Monday of the ISO week containing `date`.
 * Monday = 1, ..., Sunday = 7 in the ISO convention; JS has Sun=0, Mon=1..Sat=6.
 */
export function mondayOf(date: Date): Date {
    const d = cloneDate(date);
    d.setHours(0, 0, 0, 0);
    const dow = d.getDay(); // Sun=0..Sat=6
    const offsetToMonday = dow === 0 ? -6 : 1 - dow; // Sunday → -6, Monday → 0, Tuesday → -1 ...
    d.setDate(d.getDate() + offsetToMonday);
    return d;
}

/**
 * The ordinal position of `monday` among Mondays in its own month.
 * Example: in April 2026 the Mondays are Apr 6, 13, 20, 27 → weekNumberInMonth
 * returns 1, 2, 3, 4 respectively.
 */
export function weekNumberInMonth(monday: Date): number {
    const year = monday.getFullYear();
    const month = monday.getMonth(); // 0-indexed for Date constructor
    const firstOfMonth = new Date(year, month, 1);
    const dow = firstOfMonth.getDay(); // Sun=0..Sat=6
    const daysToFirstMonday = (1 - dow + 7) % 7; // 0..6
    const firstMonday = new Date(year, month, 1 + daysToFirstMonday);
    const diffDays = Math.round(
        (monday.getTime() - firstMonday.getTime()) / (24 * 60 * 60 * 1000)
    );
    return Math.floor(diffDays / 7) + 1;
}

/**
 * Build a virtual month row from a (year, 1-based month) pair.
 * Exported so the caller can re-build a row for a specific period without
 * re-running the full generator.
 */
export function virtualMonthFor(year: number, month: number): VirtualMonth {
    return {
        year,
        month,
        name: `${MONTH_NAMES[month - 1]} ${year}`,
        key: `${year}-${String(month).padStart(2, '0')}`,
    };
}

/**
 * `count` consecutive months starting with the month of `anchor` (default: today).
 * Always inclusive of the anchor's own month, so `nextNMonths(6)` in April returns
 * Apr..Sep of that year.
 */
export function nextNMonths(count: number, anchor: Date = new Date()): VirtualMonth[] {
    const out: VirtualMonth[] = [];
    const start = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    for (let i = 0; i < count; i++) {
        const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
        out.push(virtualMonthFor(d.getFullYear(), d.getMonth() + 1));
    }
    return out;
}

/**
 * Build a virtual week row from a Monday Date.
 */
export function virtualWeekFor(monday: Date): VirtualWeek {
    const m = cloneDate(monday);
    m.setHours(0, 0, 0, 0);
    const sunday = cloneDate(m);
    sunday.setDate(sunday.getDate() + 6);
    const weekNumber = weekNumberInMonth(m);
    return {
        year: m.getFullYear(),
        month: m.getMonth() + 1,
        weekNumber,
        startDate: toIsoDate(m),
        endDate: toIsoDate(sunday),
        name: `Week ${weekNumber}`,
        key: toIsoDate(m),
    };
}

/**
 * `count` consecutive Monday-anchored weeks starting with the Monday of
 * `anchor`'s week (default: today). So if today is Sunday Apr 19 2026,
 * `nextNWeeks(6)` returns weeks starting Apr 13, Apr 20, Apr 27, May 4,
 * May 11, May 18.
 */
export function nextNWeeks(count: number, anchor: Date = new Date()): VirtualWeek[] {
    const out: VirtualWeek[] = [];
    const firstMonday = mondayOf(anchor);
    for (let i = 0; i < count; i++) {
        const d = cloneDate(firstMonday);
        d.setDate(d.getDate() + i * 7);
        out.push(virtualWeekFor(d));
    }
    return out;
}

/**
 * Every Monday-anchored week whose Monday falls inside (year, 1-based month).
 * A calendar month has 4 or 5 such weeks. Used for "Move to Weekly" where
 * tasks are already bound to a specific monthly plan, so cross-month weeks
 * aren't valid targets.
 */
export function weeksForMonth(year: number, month: number): VirtualWeek[] {
    const out: VirtualWeek[] = [];
    const firstOfMonth = new Date(year, month - 1, 1);
    const dow = firstOfMonth.getDay();
    const daysToFirstMonday = (1 - dow + 7) % 7;
    let cursor = new Date(year, month - 1, 1 + daysToFirstMonday);
    while (cursor.getMonth() === month - 1 && cursor.getFullYear() === year) {
        out.push(virtualWeekFor(cursor));
        cursor = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 7);
    }
    return out;
}

/**
 * Given a preferred `week_number` and the list of already-taken numbers in
 * the same month, return the preferred one if free, else the smallest free
 * slot in 1..6. Throws if all six are used (the schema's hard cap).
 *
 * The "preferred number may already be in use" case happens when an older
 * weekly plan was created by the legacy modal flow that assigned week
 * numbers sequentially by creation order rather than by calendar position.
 */
export function nextAvailableWeekNumber(used: Iterable<number>, preferred: number): number {
    const taken = new Set(used);
    if (!taken.has(preferred)) return preferred;
    for (let n = 1; n <= 6; n++) {
        if (!taken.has(n)) return n;
    }
    throw new Error('Monthly plan already has 6 weeks; no slot available.');
}
